import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Database,
  Server,
  Cloud,
  Code2,
  Layers3,
  Rocket,
  Sparkles,
  Terminal,
  Users,
  BriefcaseBusiness,
  Clock3,
  Award,
  ChevronDown,
  BarChart3,
  Workflow,
  GitBranch,
  ShieldCheck,
  Cpu,
  Zap,
} from "lucide-react";

import Layout from "../../components/shared/layout/layout";
import CourseDescription from "./component/course-description";
import { softwareEngPageFirstImg } from "../../assets";

const DataEngineering = () => {
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 35,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const features = [
    {
      icon: Database,
      title: "Work With Data",
      text: "Learn how to collect, organize, transform and manage data at scale.",
    },
    {
      icon: Workflow,
      title: "Build Data Pipelines",
      text: "Design reliable pipelines that move data from multiple sources into useful systems.",
    },
    {
      icon: Cloud,
      title: "Cloud & Big Data",
      text: "Understand modern cloud platforms, distributed systems and scalable data infrastructure.",
    },
    {
      icon: BarChart3,
      title: "Analytics Ready",
      text: "Prepare clean, reliable datasets that power analytics, dashboards and AI applications.",
    },
  ];

  const curriculum = [
    "Python for Data Engineering",
    "SQL & Relational Databases",
    "Database Design & Data Modelling",
    "ETL & ELT Pipelines",
    "Data Cleaning & Transformation",
    "APIs & Data Extraction",
    "Git & Version Control",
    "Apache Spark & Big Data",
    "Data Warehousing",
    "Data Lakes & Lakehouse Architecture",
    "Airflow & Workflow Orchestration",
    "Cloud Data Engineering",
    "Docker & Containerisation",
    "Data Quality & Validation",
    "Data Security & Governance",
    "Real-World Data Engineering Projects",
  ];

  const careerPaths = [
    "Data Engineer",
    "Junior Data Engineer",
    "Analytics Engineer",
    "ETL Developer",
    "Big Data Engineer",
    "Cloud Data Engineer",
    "Data Platform Engineer",
    "Database Developer",
  ];

  const technologies = [
    {
      icon: Code2,
      name: "Python",
    },
    {
      icon: Database,
      name: "SQL",
    },
    {
      icon: Zap,
      name: "Apache Spark",
    },
    {
      icon: Workflow,
      name: "Airflow",
    },
    {
      icon: Cloud,
      name: "Cloud",
    },
    {
      icon: Terminal,
      name: "Linux",
    },
  ];

  return (
    <Layout>
      <div className="overflow-hidden bg-white text-[#07141a]">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="relative overflow-hidden bg-[#07141a]">

          {/* Ambient Background */}

          <div className="pointer-events-none absolute inset-0">

            <div className="absolute -left-48 -top-48 h-[600px] w-[600px] rounded-full bg-[#59b5cd]/20 blur-[150px]" />

            <div className="absolute -right-48 top-20 h-[600px] w-[600px] rounded-full bg-[#ecb32c]/10 blur-[150px]" />

            <div className="absolute bottom-[-250px] left-1/3 h-[500px] w-[500px] rounded-full bg-[#59b5cd]/10 blur-[140px]" />

          </div>

          {/* Grid */}

          <div
            className="pointer-events-none absolute inset-0 opacity-[0.045]"
            style={{
              backgroundImage:
                "linear-gradient(#59b5cd 1px, transparent 1px), linear-gradient(90deg, #59b5cd 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />

          <div className="relative mx-auto max-w-7xl px-6 py-24 lg:py-32">

            <div className="grid items-center gap-16 lg:grid-cols-2">

              {/* LEFT */}

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
              >

                <div
                  className="
                    mb-7
                    inline-flex
                    items-center
                    gap-2
                    rounded-full
                    border
                    border-[#59b5cd]/30
                    bg-[#59b5cd]/10
                    px-4
                    py-2
                    text-sm
                    font-semibold
                    text-[#59b5cd]
                  "
                >
                  <Sparkles size={16} />

                  Zulfah Academy · Data Engineering
                </div>

                <h1
                  className="
                    text-5xl
                    font-black
                    leading-[0.98]
                    tracking-tight
                    text-white
                    sm:text-6xl
                    lg:text-[76px]
                  "
                >
                  Build.

                  <br />

                  <span className="text-[#59b5cd]">
                    Move.
                  </span>

                  <br />

                  <span className="text-[#ecb32c]">
                    Scale.
                  </span>
                </h1>

                <p
                  className="
                    mt-8
                    max-w-xl
                    text-lg
                    leading-relaxed
                    text-white/65
                    md:text-xl
                  "
                >
                  Learn how to turn raw data into reliable,
                  scalable data systems that power businesses,
                  analytics and artificial intelligence.
                </p>

                <div className="mt-9 flex flex-wrap gap-4">

                  <Link to="/get-started">

                    <button
                      className="
                        group
                        flex
                        items-center
                        gap-3
                        rounded-xl
                        bg-[#ecb32c]
                        px-7
                        py-4
                        font-bold
                        text-[#07141a]
                        shadow-lg
                        shadow-[#ecb32c]/20
                        transition-all
                        hover:bg-[#f2c34e]
                      "
                    >
                      Start Your Journey

                      <ArrowRight
                        size={20}
                        className="transition group-hover:translate-x-1"
                      />
                    </button>

                  </Link>

                  <a href="#curriculum">

                    <button
                      className="
                        flex
                        items-center
                        gap-2
                        rounded-xl
                        border
                        border-white/15
                        bg-white/5
                        px-7
                        py-4
                        font-semibold
                        text-white
                        transition
                        hover:bg-white/10
                      "
                    >
                      Explore Curriculum

                      <ChevronDown size={18} />
                    </button>

                  </a>

                </div>

                {/* Stats */}

                <div
                  className="
                    mt-12
                    flex
                    flex-wrap
                    gap-8
                    border-t
                    border-white/10
                    pt-8
                  "
                >

                  <div>
                    <p className="text-2xl font-bold text-white">
                      Practical
                    </p>

                    <p className="text-sm text-white/40">
                      Learning
                    </p>
                  </div>

                  <div>
                    <p className="text-2xl font-bold text-white">
                      Beginner
                    </p>

                    <p className="text-sm text-white/40">
                      Friendly
                    </p>
                  </div>

                  <div>
                    <p className="text-2xl font-bold text-white">
                      Real Data
                    </p>

                    <p className="text-sm text-white/40">
                      Projects
                    </p>
                  </div>

                </div>

              </motion.div>


              {/* RIGHT VISUAL */}

              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.9,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                transition={{
                  duration: 0.9,
                }}
                className="relative"
              >

                <div
                  className="
                    relative
                    z-10
                    rounded-[30px]
                    border
                    border-white/10
                    bg-white/5
                    p-3
                    shadow-2xl
                    backdrop-blur-xl
                  "
                >

                  <div
                    className="
                      relative
                      overflow-hidden
                      rounded-[22px]
                    "
                  >

                    <img
                      src={softwareEngPageFirstImg}
                      alt="Data Engineering at Zulfah Academy"
                      className="
                        h-[430px]
                        w-full
                        object-cover
                      "
                    />

                    <div
                      className="
                        absolute
                        inset-0
                        bg-gradient-to-t
                        from-[#07141a]/90
                        via-[#07141a]/10
                        to-transparent
                      "
                    />

                    <div className="absolute bottom-6 left-6 right-6">

                      <div className="flex items-center gap-3">

                        <div
                          className="
                            flex
                            h-12
                            w-12
                            items-center
                            justify-center
                            rounded-xl
                            bg-[#ecb32c]
                            text-[#07141a]
                          "
                        >
                          <Database size={23} />
                        </div>

                        <div>

                          <p className="font-bold text-white">
                            Data Engineering Mode
                          </p>

                          <p className="text-sm text-white/50">
                            Raw data → reliable systems
                          </p>

                        </div>

                      </div>

                    </div>

                  </div>

                </div>


                {/* Floating Card */}

                <motion.div
                  animate={{
                    y: [0, -12, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="
                    absolute
                    -left-8
                    bottom-10
                    z-20
                    hidden
                    items-center
                    gap-3
                    rounded-2xl
                    bg-white
                    p-4
                    shadow-2xl
                    md:flex
                  "
                >

                  <div
                    className="
                      flex
                      h-11
                      w-11
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#59b5cd]/15
                    "
                  >
                    <Workflow
                      size={22}
                      className="text-[#076c93]"
                    />
                  </div>

                  <div>

                    <p className="font-bold text-[#07141a]">
                      Build Pipelines
                    </p>

                    <p className="text-xs text-gray-500">
                      Automate data workflows
                    </p>

                  </div>

                </motion.div>


                {/* Floating Right */}

                <motion.div
                  animate={{
                    y: [0, 10, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="
                    absolute
                    -right-8
                    top-12
                    z-20
                    hidden
                    items-center
                    gap-3
                    rounded-2xl
                    bg-[#ecb32c]
                    p-4
                    shadow-xl
                    md:flex
                  "
                >

                  <BarChart3 size={25} />

                  <div>

                    <p className="font-bold">
                      Data That Matters
                    </p>

                    <p className="text-xs opacity-70">
                      Power analytics & AI
                    </p>

                  </div>

                </motion.div>

              </motion.div>

            </div>

          </div>

        </section>


        {/* =====================================================
            COURSE SNAPSHOT
        ===================================================== */}

        <section className="relative z-20 -mt-12 px-6">

          <div className="mx-auto max-w-6xl">

            <div
              className="
                grid
                grid-cols-2
                overflow-hidden
                rounded-3xl
                border
                border-gray-100
                bg-white
                shadow-2xl
                md:grid-cols-4
              "
            >

              {[
                {
                  icon: Clock3,
                  title: "Practical",
                  text: "Training",
                },
                {
                  icon: Database,
                  title: "Data",
                  text: "Engineering",
                },
                {
                  icon: BriefcaseBusiness,
                  title: "Projects",
                  text: "Portfolio Ready",
                },
                {
                  icon: Award,
                  title: "Certificate",
                  text: "Upon Completion",
                },
              ].map((item, index) => {

                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="
                      flex
                      items-center
                      gap-4
                      border-b
                      border-gray-100
                      p-6
                      md:border-b-0
                      md:border-r
                      md:p-8
                      last:border-0
                    "
                  >

                    <div
                      className="
                        flex
                        h-11
                        w-11
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-[#59b5cd]/10
                      "
                    >
                      <Icon
                        size={21}
                        className="text-[#076c93]"
                      />
                    </div>

                    <div>

                      <p className="text-lg font-bold">
                        {item.title}
                      </p>

                      <p className="text-sm text-gray-500">
                        {item.text}
                      </p>

                    </div>

                  </div>
                );

              })}

            </div>

          </div>

        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}

        <section className="mx-auto max-w-7xl px-6 py-28">

          <motion.div
            variants={fadeUp}
            whileInView="visible"
            initial="hidden"
            viewport={{
              once: true,
            }}
            className="max-w-4xl"
          >

            <p
              className="
                mb-4
                text-sm
                font-bold
                uppercase
                tracking-[0.25em]
                text-[#076c93]
              "
            >
              Why Data Engineering?
            </p>

            <h2
              className="
                text-4xl
                font-black
                leading-tight
                md:text-6xl
              "
            >
              Data is everywhere.

              <span className="block text-[#59b5cd]">
                Someone has to make it useful.
              </span>
            </h2>

            <p
              className="
                mt-7
                max-w-3xl
                text-lg
                leading-relaxed
                text-gray-600
              "
            >
              Modern organizations generate enormous amounts of
              information every day. Data engineers build the systems
              that collect, process, transform and deliver that data
              so businesses, analysts and AI systems can actually use it.
            </p>

          </motion.div>


          {/* FEATURES */}

          <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-4">

            {features.map((feature, index) => {

              const Icon = feature.icon;

              return (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileInView="visible"
                  initial="hidden"
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    delay: index * 0.1,
                  }}
                  className="
                    group
                    rounded-3xl
                    border
                    border-gray-100
                    bg-white
                    p-7
                    transition-all
                    hover:border-[#59b5cd]/40
                    hover:shadow-xl
                  "
                >

                  <div
                    className="
                      mb-6
                      flex
                      h-12
                      w-12
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#59b5cd]/10
                      transition
                      group-hover:bg-[#ecb32c]
                    "
                  >
                    <Icon size={23} />
                  </div>

                  <h3 className="text-xl font-bold">
                    {feature.title}
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-gray-500">
                    {feature.text}
                  </p>

                </motion.div>
              );

            })}

          </div>

        </section>


        {/* =====================================================
            DARK DATA PIPELINE SECTION
        ===================================================== */}

        <section className="relative overflow-hidden bg-[#07141a]">

          <div
            className="
              absolute
              -left-40
              top-0
              h-[500px]
              w-[500px]
              rounded-full
              bg-[#59b5cd]/10
              blur-[130px]
            "
          />

          <div className="relative mx-auto max-w-7xl px-6 py-28">

            <div className="grid items-center gap-16 lg:grid-cols-2">

              <div>

                <p
                  className="
                    text-sm
                    font-bold
                    uppercase
                    tracking-[0.25em]
                    text-[#ecb32c]
                  "
                >
                  The Zulfah Approach
                </p>

                <h2
                  className="
                    mt-5
                    text-4xl
                    font-black
                    leading-tight
                    text-white
                    md:text-6xl
                  "
                >
                  Don't just
                  <span className="text-[#59b5cd]">
                    {" "}store data.
                  </span>

                  <br />

                  <span className="text-[#ecb32c]">
                    Make it flow.
                  </span>
                </h2>

                <p
                  className="
                    mt-7
                    max-w-xl
                    text-lg
                    leading-relaxed
                    text-white/55
                  "
                >
                  You'll learn how data moves through modern
                  infrastructure — from collection and ingestion
                  to transformation, storage and delivery.
                </p>

                <div className="mt-9 space-y-4">

                  {[
                    "Build automated ETL & ELT pipelines",
                    "Work with relational and distributed databases",
                    "Process large datasets with modern tools",
                    "Design production-style data architectures",
                  ].map((item, index) => (

                    <div
                      key={index}
                      className="flex items-center gap-3 text-white"
                    >

                      <CheckCircle2
                        size={20}
                        className="shrink-0 text-[#ecb32c]"
                      />

                      <span>{item}</span>

                    </div>

                  ))}

                </div>

              </div>


              {/* DATA PIPELINE VISUAL */}

              <div
                className="
                  overflow-hidden
                  rounded-3xl
                  border
                  border-white/10
                  bg-white/[0.04]
                  shadow-2xl
                  backdrop-blur-xl
                "
              >

                <div
                  className="
                    flex
                    items-center
                    gap-2
                    border-b
                    border-white/10
                    px-5
                    py-4
                  "
                >

                  <span className="h-3 w-3 rounded-full bg-red-400" />
                  <span className="h-3 w-3 rounded-full bg-yellow-400" />
                  <span className="h-3 w-3 rounded-full bg-green-400" />

                  <span className="ml-3 text-xs text-white/30">
                    data-pipeline
                  </span>

                </div>


                <div className="space-y-5 p-7">

                  {[
                    {
                      icon: Server,
                      title: "Data Sources",
                      text: "APIs · Apps · Databases",
                    },
                    {
                      icon: GitBranch,
                      title: "Ingestion",
                      text: "Extract · Stream · Load",
                    },
                    {
                      icon: Cpu,
                      title: "Processing",
                      text: "Transform · Validate · Clean",
                    },
                    {
                      icon: Database,
                      title: "Data Platform",
                      text: "Warehouse · Lake · Lakehouse",
                    },
                    {
                      icon: BarChart3,
                      title: "Business & AI",
                      text: "Analytics · ML · Applications",
                    },
                  ].map((item, index) => {

                    const Icon = item.icon;

                    return (
                      <motion.div
                        key={index}
                        initial={{
                          opacity: 0,
                          x: 20,
                        }}
                        whileInView={{
                          opacity: 1,
                          x: 0,
                        }}
                        viewport={{
                          once: true,
                        }}
                        transition={{
                          delay: index * 0.1,
                        }}
                        className="relative flex items-center gap-4"
                      >

                        <div
                          className="
                            flex
                            h-11
                            w-11
                            shrink-0
                            items-center
                            justify-center
                            rounded-xl
                            bg-[#59b5cd]/10
                            text-[#59b5cd]
                          "
                        >
                          <Icon size={20} />
                        </div>

                        <div>

                          <p className="font-bold text-white">
                            {item.title}
                          </p>

                          <p className="text-xs text-white/35">
                            {item.text}
                          </p>

                        </div>

                        {index < 4 && (
                          <div
                            className="
                              absolute
                              left-[21px]
                              top-[45px]
                              h-5
                              border-l
                              border-dashed
                              border-[#59b5cd]/30
                            "
                          />
                        )}

                      </motion.div>
                    );

                  })}

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            TECHNOLOGY STACK
        ===================================================== */}

        <section className="bg-[#f8fafb]">

          <div className="mx-auto max-w-7xl px-6 py-24">

            <div className="text-center">

              <p
                className="
                  text-sm
                  font-bold
                  uppercase
                  tracking-[0.25em]
                  text-[#076c93]
                "
              >
                Your Data Toolkit
              </p>

              <h2
                className="
                  mt-4
                  text-4xl
                  font-black
                  md:text-5xl
                "
              >
                Learn the tools
                <span className="text-[#ecb32c]">
                  {" "}behind modern data.
                </span>
              </h2>

            </div>


            <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">

              {technologies.map((tech, index) => {

                const Icon = tech.icon;

                return (
                  <motion.div
                    key={index}
                    whileHover={{
                      y: -5,
                    }}
                    className="
                      flex
                      flex-col
                      items-center
                      justify-center
                      rounded-2xl
                      border
                      border-gray-100
                      bg-white
                      p-6
                      shadow-sm
                      transition
                      hover:shadow-lg
                    "
                  >

                    <Icon
                      size={28}
                      className="text-[#076c93]"
                    />

                    <p className="mt-4 font-bold text-gray-800">
                      {tech.name}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>


        {/* =====================================================
            CURRICULUM
        ===================================================== */}

        <section
          id="curriculum"
          className="mx-auto max-w-7xl px-6 py-28"
        >

          <div className="mx-auto max-w-3xl text-center">

            <p
              className="
                text-sm
                font-bold
                uppercase
                tracking-[0.25em]
                text-[#076c93]
              "
            >
              What You'll Learn
            </p>

            <h2
              className="
                mt-4
                text-4xl
                font-black
                md:text-6xl
              "
            >
              Your data engineering
              <span className="text-[#ecb32c]">
                {" "}toolkit.
              </span>
            </h2>

            <p className="mt-6 text-lg text-gray-500">
              A practical curriculum designed to take you from
              data fundamentals to building production-style
              pipelines and data platforms.
            </p>

          </div>


          <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">

            {curriculum.map((item, index) => (

              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 15,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  delay: index * 0.03,
                }}
                whileHover={{
                  y: -4,
                }}
                className="
                  flex
                  items-center
                  gap-4
                  rounded-2xl
                  border
                  border-gray-100
                  bg-[#f8fbfc]
                  p-5
                  transition
                  hover:border-[#59b5cd]/40
                  hover:shadow-md
                "
              >

                <div
                  className="
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-[#59b5cd]/10
                  "
                >

                  <CheckCircle2
                    size={18}
                    className="text-[#076c93]"
                  />

                </div>

                <span className="font-semibold text-gray-800">
                  {item}
                </span>

              </motion.div>

            ))}

          </div>

        </section>


        {/* =====================================================
            CAREER PATHS
        ===================================================== */}

        <section className="bg-[#f6fafb]">

          <div className="mx-auto max-w-7xl px-6 py-28">

            <div className="grid items-center gap-16 lg:grid-cols-2">

              <div>

                <p
                  className="
                    text-sm
                    font-bold
                    uppercase
                    tracking-[0.25em]
                    text-[#076c93]
                  "
                >
                  Where It Can Take You
                </p>

                <h2
                  className="
                    mt-4
                    text-4xl
                    font-black
                    md:text-5xl
                  "
                >
                  One data foundation.

                  <span className="block text-[#59b5cd]">
                    Multiple opportunities.
                  </span>
                </h2>

                <p
                  className="
                    mt-6
                    max-w-xl
                    text-lg
                    leading-relaxed
                    text-gray-600
                  "
                >
                  Data engineering skills are increasingly
                  important across fintech, healthcare, logistics,
                  telecommunications, e-commerce, artificial
                  intelligence and enterprise technology.
                </p>

                <Link to="/get-started">

                  <button
                    className="
                      mt-8
                      flex
                      items-center
                      gap-3
                      rounded-xl
                      bg-[#07141a]
                      px-7
                      py-4
                      font-bold
                      text-white
                      transition
                      hover:bg-[#076c93]
                    "
                  >
                    Begin Your Journey

                    <ArrowRight size={19} />
                  </button>

                </Link>

              </div>


              <div className="grid gap-4 sm:grid-cols-2">

                {careerPaths.map((career, index) => (

                  <motion.div
                    key={index}
                    whileHover={{
                      y: -5,
                    }}
                    className="
                      rounded-2xl
                      border
                      border-gray-100
                      bg-white
                      p-6
                      shadow-sm
                      transition
                      hover:shadow-lg
                    "
                  >

                    <div
                      className="
                        mb-5
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-xl
                        bg-[#ecb32c]/15
                      "
                    >

                      <BriefcaseBusiness
                        size={20}
                        className="text-[#a87500]"
                      />

                    </div>

                    <h3 className="font-bold">
                      {career}
                    </h3>

                    <p className="mt-2 text-xs text-gray-400">
                      Career pathway
                    </p>

                  </motion.div>

                ))}

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            PROGRAM DETAILS / COURSE DESCRIPTION
        ===================================================== */}

        <section className="bg-[#f8fafb]">

          <div className="mx-auto max-w-7xl px-6 py-20">

            <div className="mb-12 text-center">

              <p
                className="
                  text-sm
                  font-bold
                  uppercase
                  tracking-[0.25em]
                  text-[#076c93]
                "
              >
                Program Details
              </p>

              <h2
                className="
                  mt-4
                  text-4xl
                  font-black
                  md:text-5xl
                "
              >
                Ready to start

                <span className="text-[#ecb32c]">
                  {" "}building with data?
                </span>
              </h2>

              <p className="mx-auto mt-4 max-w-2xl text-gray-500">
                Explore programme options, pricing and enrollment
                information below.
              </p>

            </div>


            <div
              className="
                mx-auto
                max-w-5xl
                overflow-hidden
                rounded-3xl
                border
                border-gray-100
                bg-white
                shadow-sm
              "
            >

              <CourseDescription />

            </div>

          </div>

        </section>


        {/* =====================================================
            FINAL CTA
        ===================================================== */}

        <section className="relative overflow-hidden bg-[#07141a]">

          <div className="pointer-events-none absolute inset-0">

            <div
              className="
                absolute
                -left-40
                -top-40
                h-[500px]
                w-[500px]
                rounded-full
                bg-[#59b5cd]/15
                blur-[140px]
              "
            />

            <div
              className="
                absolute
                -bottom-40
                -right-40
                h-[450px]
                w-[450px]
                rounded-full
                bg-[#ecb32c]/10
                blur-[140px]
              "
            />

          </div>


          <div
            className="
              relative
              mx-auto
              max-w-5xl
              px-6
              py-28
              text-center
            "
          >

            <div
              className="
                mb-6
                inline-flex
                items-center
                gap-2
                font-semibold
                text-[#ecb32c]
              "
            >

              <Rocket size={19} />

              Your data journey starts here.

            </div>

            <h2
              className="
                text-4xl
                font-black
                leading-tight
                text-white
                md:text-6xl
              "
            >
              Ready to become

              <span className="text-[#59b5cd]">
                {" "}a data engineer?
              </span>
            </h2>

            <p
              className="
                mx-auto
                mt-6
                max-w-2xl
                text-lg
                text-white/55
              "
            >
              Learn the skills behind the systems that power
              analytics, businesses and the next generation of AI.
            </p>

            <div className="mt-9 flex flex-wrap justify-center gap-4">

              <Link to="/get-started">

                <button
                  className="
                    flex
                    items-center
                    gap-3
                    rounded-xl
                    bg-[#ecb32c]
                    px-8
                    py-4
                    font-bold
                    text-[#07141a]
                    shadow-xl
                    transition
                    hover:scale-[1.03]
                  "
                >
                  Apply Now

                  <ArrowRight size={20} />
                </button>

              </Link>

              <a href="#curriculum">

                <button
                  className="
                    rounded-xl
                    border
                    border-white/15
                    bg-white/5
                    px-8
                    py-4
                    font-semibold
                    text-white
                    transition
                    hover:bg-white/10
                  "
                >
                  View Curriculum
                </button>

              </a>

            </div>

          </div>

        </section>

      </div>
    </Layout>
  );
};

export default DataEngineering;